import { Component, AfterViewInit, OnDestroy, ElementRef, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import * as L from 'leaflet';

@Component({
  selector: 'fikoyt-harita-yer-secici',
  standalone: true,
  imports: [CommonModule, FormsModule, NzButtonModule, NzInputModule, NzIconModule],
  template: `
    <div class="yer-secici-konteyner">
      <div #haritaDiv class="yer-secici-harita"></div>
      <div class="yer-secici-alt">
        <div class="yer-secici-bilgi">
          @if (!secilenKonum) {
            <span class="ipucu">Haritada bir konuma tıklayın</span>
          } @else {
            <span class="konum-bilgi">
              <span nz-icon nzType="environment" nzTheme="filled" style="color:#3b82f6;"></span>
              {{ secilenKonum.lat.toFixed(4) }}, {{ secilenKonum.lng.toFixed(4) }}
            </span>
          }
        </div>
        <div class="yer-isim-satir">
          <input nz-input nzSize="small" [(ngModel)]="yerIsmi" placeholder="Yer adı giriniz (ör: İstanbul)"
            (keydown.enter)="onayla()" />
          <button nz-button nzType="primary" nzSize="small" [disabled]="!yerIsmi.trim()" (click)="onayla()">
            <span nz-icon nzType="check"></span> Seç
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .yer-secici-konteyner {
      display: flex;
      flex-direction: column;
      height: 400px;
    }
    .yer-secici-harita {
      flex: 1;
      min-height: 0;
    }
    .yer-secici-alt {
      padding: 8px 12px;
      display: flex;
      flex-direction: column;
      gap: 6px;
      border-top: 1px solid #f0f0f0;
      background: #fafafa;
    }
    .yer-secici-bilgi {
      font-size: 11px;
      color: #666;
    }
    .ipucu { color: #999; font-style: italic; }
    .konum-bilgi { display: flex; align-items: center; gap: 4px; }
    .yer-isim-satir {
      display: flex;
      gap: 6px;
      align-items: center;
    }
    .yer-isim-satir input { flex: 1; }
    :host ::ng-deep .leaflet-container { z-index: 0; }
    :host ::ng-deep .leaflet-container img { max-width: none !important; max-height: none !important; }
  `],
})
export class HaritaYerSeciciComponent implements AfterViewInit, OnDestroy {
  @ViewChild('haritaDiv') haritaRef!: ElementRef<HTMLDivElement>;

  private modalRef = inject(NzModalRef);
  private modalData: any = inject(NZ_MODAL_DATA, { optional: true });
  private leafletMap: L.Map | null = null;
  private marker: L.Marker | null = null;

  secilenKonum: { lat: number; lng: number } | null = null;
  yerIsmi = '';
  private gecikmeliGeocode: any = null;

  ngAfterViewInit(): void {
    const mevcutYer = this.modalData?.mevcutYer || '';
    const mevcutKoordinat = this.modalData?.mevcutKoordinat;
    this.yerIsmi = mevcutYer;

    setTimeout(() => {
      const baslangic: [number, number] = mevcutKoordinat ? [mevcutKoordinat.lat, mevcutKoordinat.lng] : [39.0, 35.0];
      const zoom = mevcutKoordinat ? 10 : 6;
      this.leafletMap = L.map(this.haritaRef.nativeElement).setView(baslangic, zoom);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap',
      }).addTo(this.leafletMap);

      this.leafletMap.on('click', (e: L.LeafletMouseEvent) => {
        this.konumSec(e.latlng.lat, e.latlng.lng);
        this.tersGeocode(e.latlng.lat, e.latlng.lng);
      });

      // Mevcut koordinat varsa direkt göster, yoksa yer adıyla geocode
      if (mevcutKoordinat) {
        this.konumSec(mevcutKoordinat.lat, mevcutKoordinat.lng);
      } else if (mevcutYer) {
        this.mevcutYeriGoster(mevcutYer);
      }

      setTimeout(() => this.leafletMap?.invalidateSize(), 200);
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.leafletMap) {
      this.leafletMap.remove();
      this.leafletMap = null;
    }
    if (this.gecikmeliGeocode) clearTimeout(this.gecikmeliGeocode);
  }

  private konumSec(lat: number, lng: number) {
    this.secilenKonum = { lat, lng };
    if (this.marker) {
      this.marker.setLatLng([lat, lng]);
    } else if (this.leafletMap) {
      this.marker = L.marker([lat, lng]).addTo(this.leafletMap);
    }
  }

  private async tersGeocode(lat: number, lng: number) {
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10&accept-language=tr`;
      const res = await fetch(url, { headers: { 'User-Agent': 'SoyAgaci-FamilyTree/1.0' } });
      const data = await res.json();
      if (data?.address) {
        const a = data.address;
        // İlçe/şehir/ülke formatı
        const parcalar = [a.town || a.city || a.county, a.state || a.province, a.country].filter(Boolean);
        if (parcalar.length > 0) {
          this.yerIsmi = parcalar.join(', ');
        } else if (data.display_name) {
          this.yerIsmi = data.display_name.split(',').slice(0, 3).join(',').trim();
        }
      }
    } catch {}
  }

  private async mevcutYeriGoster(yer: string) {
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(yer)}&limit=1&accept-language=tr`;
      const res = await fetch(url, { headers: { 'User-Agent': 'SoyAgaci-FamilyTree/1.0' } });
      const data = await res.json();
      if (data?.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lng = parseFloat(data[0].lon);
        this.konumSec(lat, lng);
        this.leafletMap?.setView([lat, lng], 10);
      }
    } catch {}
  }

  onayla() {
    if (this.yerIsmi.trim()) {
      this.modalRef.close({
        yerIsmi: this.yerIsmi.trim(),
        koordinat: this.secilenKonum ? { lat: this.secilenKonum.lat, lng: this.secilenKonum.lng } : null,
      });
    }
  }
}
